import { CompanyType } from '@@types/api.types';
import { SelectionCard } from '@components/shared/selection-card.component';
import { withForm } from '@components/ui/form/form';
import { FormErrorMessage } from '@components/ui/form/shared.component';
import { cn } from '@lib/utils';
import { typeStepSchema } from '@schemas/company.schema';
import { Building2Icon, FlaskConicalIcon, GraduationCapIcon, RadarIcon } from 'lucide-react';
import { companyFormOptions } from './company-form-options';

// AZ-451 D9: from the admin console only Intelligence orgs are creatable, so the admin Type step
// shows the Intelligence card alone (the other types are created via the innovator sign-up flow).
const intelTypeIcon = (isSelected: boolean) => (
  <RadarIcon className={cn('size-8', isSelected ? 'text-teal-600' : 'text-[#1F1C1A] opacity-70')} />
);

const COMPANY_TYPE_OPTIONS = [
  {
    value: CompanyType.COMMERCIAL,
    primaryLabel: 'Commercial',
    secondaryLabel: 'Companies, and other commercial entities.',
    icon: (isSelected: boolean) => (
      <Building2Icon className={cn('size-8', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
  },
  {
    value: CompanyType.RESEARCH,
    primaryLabel: 'Research',
    secondaryLabel: 'Universities, non-profits, and other research institutions.',
    icon: (isSelected: boolean) => (
      <GraduationCapIcon className={cn('size-8', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
  },
  {
    value: CompanyType.RESEARCH_PROJECT,
    primaryLabel: 'Research Project',
    secondaryLabel: 'Research projects, labs, and collaborative initiatives.',
    icon: (isSelected: boolean) => (
      <FlaskConicalIcon className={cn('size-8', isSelected ? 'text-blue-700' : 'text-[#1F1C1A] opacity-70')} />
    ),
  },
  // AZ-451: Intelligence Organizations are created by Platform Admins, not via innovator onboarding,
  // so this picker intentionally excludes CompanyType.INTELLIGENCE. `as const` keeps `value` narrowed
  // to the three innovator-selectable types (matching the form schema) now that CompanyType is wider.
] as const;

const TypeStepBase = withForm({
  ...companyFormOptions,
  props: { adminIntel: false },
  render: function Render({ adminIntel, form }) {
    if (adminIntel) {
      return (
        <div className="flex flex-row flex-wrap items-center justify-center gap-4 pb-6 md:flex-nowrap">
          <SelectionCard
            icon={intelTypeIcon}
            isSelected
            onClick={() => {}}
            onKeyUp={() => {}}
            primaryLabel="Intelligence"
            secondaryLabel="Intelligence units and organizations."
            value={CompanyType.INTELLIGENCE}
          />
        </div>
      );
    }

    return (
      <form.AppForm>
        <form.AppField
          name="type"
          validators={{
            onSubmit: typeStepSchema.shape.type,
          }}
        >
          {(field) => {
            const errorEntry = field.state.meta.errorMap?.onSubmit?.[0];
            const displayError = typeof errorEntry === 'string' ? errorEntry : errorEntry?.message;

            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-row flex-wrap items-center justify-center gap-4 pb-6 md:flex-nowrap">
                  {COMPANY_TYPE_OPTIONS.map(({ value, primaryLabel, secondaryLabel, icon }) => {
                    const isSelected = field.state.value === value;
                    return (
                      <SelectionCard
                        key={value}
                        value={value}
                        icon={icon}
                        primaryLabel={primaryLabel}
                        secondaryLabel={secondaryLabel}
                        isSelected={isSelected}
                        onClick={() => field.handleChange(value)}
                        onKeyUp={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            field.handleChange(value);
                          }
                        }}
                      />
                    );
                  })}
                </div>
                {displayError && <FormErrorMessage message={displayError} />}
              </div>
            );
          }}
        </form.AppField>
      </form.AppForm>
    );
  },
});

export const TypeStep = TypeStepBase;
